import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {
  UIEventHandler,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Debouncer} from 'yougile-util'
import {Hub} from 'yougile-model'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {getAllTasksFromPrivateLists} from '@components/my-tasks/utils'
import {useForceUpdate, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {getAllMirroredHubs} from '@extensions/mirrored-tasks/get-all-mirrored-hubs'
import {MirroredTasks} from '@extensions/mirrored-tasks/mirrored-tasks'
import {Task} from '@extensions/task'
import {taskPhantomObserver} from '@extensions/task-phantom-observer'
import {useRightPanelContentStore} from '../right-panel-content/right-panel-content-store'
import {CheckList} from './checklists-new/check-list'
import {
  ChecklistsContext,
  ChecklistsStore,
  useChecklistsStore
} from './checklists-store'
import {Header} from './subtasks-new/header'
import {Subtasks} from './subtasks-new/subtasks'

interface Props {
  hub: Hub | undefined
  newBg?: boolean
  hideSubtasks?: boolean
}

const TwContainer = tw.div<{$newBg?: boolean}>`
  h-fit
  px-8
  ${p => (p.$newBg ? 'bg-background-primary' : 'bg-grey-l100')}
`

const runLatestDebouncer = new Debouncer(100)

export const ChecklistsTabBase = observer(
  ({hub, newBg, hideSubtasks}: Props) => {
    const [state, store] = useChecklistsStore()
    const force = useForceUpdate()
    const [panelState, panelStore] = useRightPanelContentStore()

    const isTaskTemplate = Task.isTaskTemplate(hub)

    useLayoutEffect(() => {
      hub && store.syncOnNew(hub)
      force()
    }, [hub])

    React.useEffect(store.rerender, [state.waitForRender])

    const opt = {filter: hub?.id}
    useSyncEvent('current', opt, store.syncOnUpdate, [hub])

    useEffect(() => {
      store.syncOnUpdate()
    }, [taskPhantomObserver.modified])

    const scrollbarContainerRef = useRef<HTMLElement>()
    useEffect(() => {
      setTimeout(() => {
        if (scrollbarContainerRef.current) {
          scrollbarContainerRef.current.scrollTop = panelState.subtasksScroll
        }
      }, 10)
    }, [hub?.id, scrollbarContainerRef.current])

    if (!hub) {
      return null
    }

    const hubIsFromPrivateLists = getAllTasksFromPrivateLists().includes(hub.id)

    const afterTaskAdded = () => {
      if (isTaskTemplate) {
        taskPhantomObserver.update()
        return
      }
      const el = document.getElementById('subtasks-container')
      el?.scrollIntoView({block: 'nearest'})
    }

    const content = (
      <>
        {!hideSubtasks && !hubIsFromPrivateLists && (
          <div id={'subtasks-container'}>
            <Subtasks
              taskId={hub.id}
              afterTaskAdded={afterTaskAdded}
              hub={hub}
            />
          </div>
        )}

        <MirroredTasksWithCaption task={hub} />

        <CheckList hub={hub} hideSubtasks={hideSubtasks} />
      </>
    )

    if (isTaskTemplate) {
      return <div>{content}</div>
    }

    const onScroll: UIEventHandler<HTMLElement> = event => {
      runLatestDebouncer.runLatest(() => {
        const el = event.target as HTMLDivElement
        const scroll = el?.scrollTop !== undefined ? el?.scrollTop : -1
        panelStore.setSubtasksScroll(scroll)
      })
    }

    return (
      <WithPerfectScrollbar
        className={cn(newBg ? 'bg-background-primary' : 'bg-grey-l100')}
        drag
        onScroll={onScroll}
        containerRef={el => {
          if (scrollbarContainerRef.current !== el) {
            scrollbarContainerRef.current = el
          }
        }}
      >
        <TwContainer $newBg={newBg}>{content}</TwContainer>
      </WithPerfectScrollbar>
    )
  }
)

export const ChecklistsTab = ({hub, newBg, hideSubtasks}: Props) => {
  const [checklistsStore] = useState(() => new ChecklistsStore())

  return (
    <ChecklistsContext.Provider value={checklistsStore}>
      <ChecklistsTabBase hub={hub} newBg={newBg} hideSubtasks={hideSubtasks} />
    </ChecklistsContext.Provider>
  )
}

const MirroredTasksWithCaption = observer((props: {task: Hub}) => {
  const {task} = props
  const mirroredHubs = getAllMirroredHubs(task)

  if (!mirroredHubs?.length) {
    return null
  }

  return (
    <div className="mt-32 pl-16 pr-12">
      <Header
        title={Lang.msg.mirroredTasks.taskMirrors}
        help={'taskMirrors'}
        className={'mb-16'}
      />

      <MirroredTasks task={task} mirroredHubs={mirroredHubs} />
    </div>
  )
})
